import React from 'react';
import { View, Text, ViewStyle, StyleSheet, Platform } from 'react-native';
import { useStyles } from '../../hooks/useStyles';
import { addColorOpacity } from '../../shared/utils/styles';

export interface BadgeProps {
  label: string;
  variant?: 'default' | 'primary' | 'success' | 'warning' | 'error' | 'subtle';
  size?: 'xs' | 'sm' | 'md';
  icon?: React.ReactNode;
  style?: ViewStyle;
}

export function Badge({ 
  label, 
  variant = 'default', 
  size = 'sm',
  icon,
  style 
}: BadgeProps) {
  const styles = useStyles();
  const { tokens, utils } = styles;
  const badgeStyles = makeBadgeStyles(tokens, utils, variant, size);

  return (
    <View style={[badgeStyles.container, style]}>
      {icon && (
        <View style={badgeStyles.iconContainer}>
          {icon}
        </View>
      )}
      <Text style={badgeStyles.text}>
        {label}
      </Text>
    </View>
  );
}

function makeBadgeStyles(
  tokens: ReturnType<typeof useStyles>['tokens'],
  utils: ReturnType<typeof useStyles>['utils'],
  variant: BadgeProps['variant'],
  size: BadgeProps['size']
) {
  const sizeConfig = {
    xs: {
      paddingHorizontal: utils.spacing.xs,
      paddingVertical: 2,
      fontSize: utils.typography.sizes.xs,
      iconSize: 10,
    },
    sm: {
      paddingHorizontal: utils.spacing.sm,
      paddingVertical: 4,
      fontSize: utils.typography.sizes.xs,
      iconSize: 12,
    },
    md: {
      paddingHorizontal: utils.spacing.md,
      paddingVertical: 6,
      fontSize: utils.typography.sizes.sm,
      iconSize: 14,
    },
  };

  const config = sizeConfig[size || 'md'];

  const variantStyles = {
    default: {
      backgroundColor: addColorOpacity(tokens.color.onSurface, 0.1),
      color: tokens.color.onSurface,
    },
    primary: {
      backgroundColor: addColorOpacity(tokens.color.primary, 0.15),
      color: tokens.color.primary,
    },
    success: {
      backgroundColor: addColorOpacity('#10b981', 0.15),
      color: '#10b981',
    },
    warning: {
      backgroundColor: addColorOpacity('#f59e0b', 0.15),
      color: '#f59e0b',
    },
    error: {
      backgroundColor: addColorOpacity(tokens.color.error, 0.15),
      color: tokens.color.error,
    },
    subtle: {
      backgroundColor: addColorOpacity(tokens.color.onSurface, 0.05),
      color: addColorOpacity(tokens.color.onSurface, 0.7),
    },
  };

  const variantStyle = variantStyles[variant || 'default'];

  return StyleSheet.create({
    container: {
      flexDirection: 'row',
      alignItems: 'center',
      alignSelf: 'flex-start',
      paddingHorizontal: config.paddingHorizontal,
      paddingVertical: config.paddingVertical,
      borderRadius: utils.radius.full,
      backgroundColor: variantStyle.backgroundColor,
      gap: utils.spacing.xs,
      ...Platform.select({
        web: {
          boxSizing: 'border-box' as any,
        },
      }),
    },
    iconContainer: {
      marginRight: -2,
    },
    text: {
      fontSize: config.fontSize,
      fontWeight: utils.typography.weights.medium,
      color: variantStyle.color,
      fontFamily: utils.typography.body.fontFamily,
      letterSpacing: variant === 'default' && size === 'sm' ? 0.3 : 0,
      textTransform: variant === 'default' && size === 'sm' ? 'uppercase' : 'none',
    },
  });
}

